/**
 * Locale resolution precedence — locale-neutral runtime truth stays unchanged.
 */

import {
  FALLBACK_LOCALE_V0,
  normalizeLocaleInput,
  resolveSafeLocale,
  type SupportedLocale,
} from './localeCanon';

export type LocalePlaneV0 = 'business' | 'public' | 'platform' | 'portal';

export interface LocaleResolutionInputV0 {
  plane: LocalePlaneV0;
  sessionSelection?: string | null;
  userPreferredLocale?: string | null;
  tenantDefaultLocale?: string | null;
  browserLocale?: string | null;
  externalUserPreferredLocale?: string | null;
  relationshipTenantDefaultLocale?: string | null;
}

export interface LocaleResolutionResultV0 {
  locale: SupportedLocale;
  source:
    | 'session'
    | 'user_preference'
    | 'tenant_default'
    | 'external_user_preference'
    | 'relationship_tenant_default'
    | 'browser'
    | 'fallback_en';
}

function pick(
  value: string | null | undefined,
  source: LocaleResolutionResultV0['source']
): LocaleResolutionResultV0 | null {
  const normalized = normalizeLocaleInput(value ?? undefined);
  if (!normalized) return null;
  return { locale: normalized, source };
}

/**
 * Exact precedence from Primary command §6.
 */
export function resolveLocalePrecedenceV0(
  input: LocaleResolutionInputV0
): LocaleResolutionResultV0 {
  if (input.plane === 'portal') {
    return (
      pick(input.sessionSelection, 'session') ??
      pick(input.externalUserPreferredLocale, 'external_user_preference') ??
      pick(input.relationshipTenantDefaultLocale, 'relationship_tenant_default') ??
      pick(input.tenantDefaultLocale, 'tenant_default') ??
      pick(input.browserLocale, 'browser') ?? {
        locale: FALLBACK_LOCALE_V0,
        source: 'fallback_en',
      }
    );
  }

  if (input.plane === 'public' || input.plane === 'platform') {
    return (
      pick(input.sessionSelection, 'session') ??
      pick(input.browserLocale, 'browser') ?? {
        locale: FALLBACK_LOCALE_V0,
        source: 'fallback_en',
      }
    );
  }

  // Authenticated business plane
  return (
    pick(input.sessionSelection, 'session') ??
    pick(input.userPreferredLocale, 'user_preference') ??
    pick(input.tenantDefaultLocale, 'tenant_default') ??
    pick(input.browserLocale, 'browser') ?? {
      locale: FALLBACK_LOCALE_V0,
      source: 'fallback_en',
    }
  );
}

export function readBrowserLocaleV0(): SupportedLocale | null {
  if (typeof navigator === 'undefined') return null;
  return normalizeLocaleInput(navigator.language);
}

export function assertInvalidLocaleFallsBackToEn(value: string): SupportedLocale {
  return resolveSafeLocale(value);
}
